import { useToggle } from "@react-hookz/web";
import { type FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { usePlusDetailedListingPopup } from "scmp-app/components/plus/detailed-listing-popup/hooks";
import type { plusAuthorCardAuthor$key } from "scmp-app/queries/__generated__/plusAuthorCardAuthor.graphql";

import {
  Expertise,
  StyleAuthorCardContainer,
  StyleAuthorInfoContainer,
  StyleAvatarContainer,
  StyledAuthorAvatar,
  StyledAuthorBio,
  StyledStyleAuthorName,
  StyleNameContainer,
} from "./styles";

export type Props = {
  reference: plusAuthorCardAuthor$key;
};

export const PlusAuthorCard: FunctionComponent<Props> = ({ reference: reference_ }) => {
  const [isShowMore, toggleShowMore] = useToggle(false);
  const [canExpand, setCanExpand] = useState(false);
  const bioReference = useRef<HTMLDivElement | null>(null);
  const { handleOpen } = usePlusDetailedListingPopup();
  const author = useFragment(
    graphql`
      fragment plusAuthorCardAuthor on Author {
        ...authorAvatarAuthor
        ...authorNameAuthor
        ...authorBioAuthor
        areaOfExpertise
      }
    `,
    reference_,
  );

  const handleAuthorClick = (entityUuid: string) => {
    handleOpen({ entityUuid, type: "author" });
  };

  useEffect(() => {
    const bioElement = bioReference.current;

    if (!bioElement) {
      setCanExpand(false);
      return;
    }

    const lineHeightPx = 16;
    const maxVisibleHeight = lineHeightPx * 2;
    const { scrollHeight } = bioElement;

    setCanExpand(scrollHeight > maxVisibleHeight + 1);
  }, [author]);

  const handleToggleShowMore = useCallback(() => {
    if (!canExpand) {
      return;
    }

    toggleShowMore();
  }, [canExpand, toggleShowMore]);

  return (
    <StyleAuthorCardContainer>
      <StyleAvatarContainer>
        <StyledAuthorAvatar
          height={120}
          onClick={handleAuthorClick}
          placeholder
          reference={author}
          width={120}
          withoutLink
        />
      </StyleAvatarContainer>
      <StyleAuthorInfoContainer>
        <StyleNameContainer>
          <StyledStyleAuthorName onClick={handleAuthorClick} reference={author} withoutLink />
          {!!author.areaOfExpertise?.length && (
            <>
              <span>|</span>
              <Expertise>{author.areaOfExpertise.join(", ").trim()}</Expertise>
            </>
          )}
        </StyleNameContainer>
        <StyledAuthorBio
          $canExpand={canExpand}
          $isExpanded={isShowMore}
          onClick={handleToggleShowMore}
          ref={bioReference}
          reference={author}
        />
      </StyleAuthorInfoContainer>
    </StyleAuthorCardContainer>
  );
};

PlusAuthorCard.displayName = "PlusAuthorCard";
